import { ListPage } from '@components/molecules';
import { DatePickerElement } from 'react-hook-form-mui/date-pickers';
import { Box, Chip, Grid } from '@mui/material';
import API from '@apps/nnc/services/actionplan';
import { ModelProps } from '@/types';
import { useTranslation } from 'react-i18next';
import { JSX, useEffect, useState } from 'react';
import { NncSchema, NncSearchSchema } from '@apps/nnc/schemas/nnc';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import UnitAPI from '@apps/localization/services/unit';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { AutocompleteElement, TextFieldElement } from 'react-hook-form-mui';
import CircleIcon from '@mui/icons-material/Circle';
import CountryAPI from '@apps/localization/services/country';

export default function EntriesListPage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();
  const defaultFormContext = useForm<z.infer<z.ZodTypeAny>>();
  const start = defaultFormContext.watch('start_date', null);
  const end = defaultFormContext.watch('end_date', null);
  const [refetch, setRefresh] = useState(false);

  const opt = [
    {
      id: 1,
      label: t('Pending'),
    },
    {
      id: 2,
      label: t('On Going'),
    },
    {
      id: 3,
      label: t('Completed'),
    },
  ];

  const columns = [
    {
      field: 'reference_number',
      headerName: t('Action Plan'),
      flex: 1,
    },
    {
      field: 'theme',
      headerName: t('Action Plan Theme'),
      flex: 2,
    },
    {
      field: 'actions_count',
      headerName: t('Actions Count'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box display={'flex'} sx={{ height: '100%' }} alignItems="center">
            <Box
              sx={{
                background: '#00d160',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                color: '#fff',
                fontWeight: 'bold',
                width: '80px',
                height: '30px',
              }}
            >
              {params?.row?.actions_count}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: t('Created At'),
      width: 150,
    },
    {
      field: 'finished_at',
      headerName: t('Finished At'),
      width: 150,
    },
    {
      field: 'duration',
      headerName: t('Duration'),
      width: 150,

      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box display={'flex'} sx={{ height: '100%' }} alignItems="center">
            <Box
              sx={{
                background: '#00d160',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                color: '#fff',
                fontWeight: 'bold',
                width: '80px',
                height: '30px',
              }}
            >
              {params?.row?.duration}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: t('Status'),
      width: 130,

      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            color={
              params?.row.status?.id == 1
                ? 'info'
                : params?.row.status?.id == 2
                  ? 'warning'
                  : params?.row.status?.id == 3
                    ? 'success'
                    : 'error'
            }
            icon={<CircleIcon sx={{ color: '#ffffff80 !important' }} />}
            label={params?.row.status?.label}
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (!start && !end) {
      setRefresh(!refetch);
    }
  }, [start, end]);

  return (
    <>
      <Box sx={{ position: 'relative', pb: 10 }}>
        <ListPage
          formcontext={defaultFormContext}
          schema={NncSchema}
          schemaSearch={NncSearchSchema}
          title={t('All Action Plans')}
          api={API}
          columns={columns}
          modelProps={modelProps}
          // transformQueryParams={(params) => ({
          //   ...params,
          //   status: params.status ? params.status : null,
          // })}
          disableAdd
          disableDelete
          effect={refetch}
          enablePersist={true}
          persistKey="actionplans-list"
        >
          <Grid size={{ xs: 12, md: 3 }}>
            <TextFieldElement name="search" label={t('Search')} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, md: 1.8 }}>
            <DatePickerElement
              name="start_date"
              label={t('Start Date')}
              inputProps={{ sx: { width: '100%' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 1.9 }}>
            <DatePickerElement
              name="end_date"
              label={t('End Date')}
              inputProps={{ sx: { width: '100%' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3.8 }}>
            <AsyncAutocompleteElement
              api={CountryAPI.select}
              name="country"
              label={t('Country')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3.8 }}>
            <AsyncAutocompleteElement
              api={UnitAPI.select}
              name="unit"
              label={t('Unit')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3.95 }}>
            <AutocompleteElement
              name="status"
              label={t('Status')}
              multiple
              options={opt}
            />
          </Grid>
        </ListPage>
      </Box>
    </>
  );
}
